import { Capacitor } from '@capacitor/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro'
import { useEffect, useRef } from 'react'
import { useRecoilValue } from 'recoil'
import styled, { useTheme } from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'

import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { P, Span } from '@/@bothrs-ui/web/components/typography/Body'
import { breakpoints } from '@/config/layout'
import { mixpanel } from '@/mixpanel'
import { LoginForm } from '@/modules/auth/components/login/login-form/LoginForm'
import { BiometricsAppPermission } from '@/modules/biometrics/state/biometrics.atom'
import { useAdminMessage } from '@/modules/healthblocks/hooks/useAdminMessage'
import { useServer } from '@/modules/healthblocks/ServerProvider'
import { AdminMessageCard } from '@/modules/shared/components/admin-message-card/AdminMessageCard'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { NativeStoreButton } from '@/modules/shared/components/native-store-button/NativeStoreButton'
import { useSwitchHospital } from '@/modules/shared/hooks/useSwitchHospital'
import { modeAtom, useQaClick } from '@/utils/qa-mode'
import { useTranslated } from '@/utils/translation'
import { AuthLayout } from '../../components/auth-layout/AuthLayout'
import { AppLockedContent } from '../../components/login/app-locked-content/AppLockedContent'
import { EHealthLoginContent } from '../../components/login/ehealth-login-content/EHealthLoginContent'
import { StLogo } from '../../components/login/login-layout/LoginLayout.styled'
import { useAuthentication } from '../../hooks/useAuthentication'

const LoginPage = ({ isAbroad = false }: { isAbroad?: boolean }) => {
  const trackedAdminMessage = useRef(false)

  // State
  // ------------------------------------------------------------------------- /
  const isQaMode = useRecoilValue(modeAtom) === 'qa'
  const handleTitlePress = useQaClick()

  // Hooks
  // ------------------------------------------------------------------------- /
  const server = useServer()

  const switchHospital = useSwitchHospital()

  // Use the new consolidated login flow
  const { isLocked, isAuthenticated, canUseBiometrics, biometricPermission } = useAuthentication() // useAuthentication now exposes useLoginFlow

  const isMobile = useMediaQuery(breakpoints.toS)
  // Poll the admin message every 3 minutes to keep it up to date
  const { adminMessage } = useAdminMessage({ refreshInterval: 1000 * 60 * 3 })
  const aboutAppUrl = useTranslated('about-app.url')

  // -- Constants --
  const image = isMobile ? '/img/happy-nurse-zoom.png' : '/img/happy-nurse.jpg'

  // Effects
  // ------------------------------------------------------------------------- /

  // Track the admin message
  useEffect(() => {
    if (adminMessage && !adminMessage.blocking && trackedAdminMessage.current === false) {
      trackedAdminMessage.current = true
      mixpanel.track('non_blocking_admin_message_show', {
        message: adminMessage.message,
      })
    } else {
      trackedAdminMessage.current = false
    }
  }, [adminMessage])

  // Render
  // ------------------------------------------------------------------------- /

  const renderAdminMessageBanner = () => {
    if (!adminMessage || adminMessage?.blocking === true) {
      return null
    }

    return <AdminMessageCard message={adminMessage.message} />
  }

  return (
    <AuthLayout image={image} HeaderComponent={<SwitchHospitalButton onClick={switchHospital} />}>
      <StMainContent>
        {server?.logo && (
          <FlexRow justifyContent='flex-start' alignItems='center'>
            <StLogo src={server.logo} alt={`Logo van ${server.display}`} />
          </FlexRow>
        )}

        {isLocked === null || biometricPermission === null || canUseBiometrics === null ? (
          <AnimatedLoader />
        ) : isLocked &&
          canUseBiometrics &&
          isAuthenticated &&
          biometricPermission === BiometricsAppPermission.GRANTED ? (
          <AppLockedContent message={renderAdminMessageBanner()} onDebugPress={handleTitlePress} />
        ) : (
          <>
            <EHealthLoginContent message={renderAdminMessageBanner()} onDebugPress={handleTitlePress}>
              {isQaMode && <LoginForm />}
            </EHealthLoginContent>
          </>
        )}
      </StMainContent>

      {isAbroad || Capacitor.isNativePlatform() ? null : (
        <StAppSection>
          <P type='BodyLargeBold' style={{ marginBottom: 0 }}>
            {t({
              id: 'login.app.title',
              message: 'Of gebruik de app',
            })}
          </P>
          <StAppButtons>
            <NativeStoreButton type='appStore' url='https://apps.apple.com/us/app/mijnziekenhuis/id1634606301' />
            <NativeStoreButton
              type='playStore'
              url='https://play.google.com/store/apps/details?id=be.mariamiddelares.app'
            />
          </StAppButtons>
        </StAppSection>
      )}

      {aboutAppUrl && (
        <StMoreInfoLink href={aboutAppUrl} target='_blank' rel='noreferrer'>
          <Span type='BodyLargeRegular' textDecoration='underline'>
            {t({
              id: 'landing.more-info',
              message: `Meer info over MijnZiekenhuis`,
            })}
          </Span>
        </StMoreInfoLink>
      )}
    </AuthLayout>
  )
}

const SwitchHospitalButton = ({ onClick }: { onClick: () => void }) => {
  const theme = useTheme()

  return (
    <StSwitchHospitalButton onClick={onClick}>
      <FontAwesomeIcon icon={['fal', 'arrows-repeat']} color={theme.Colors.Neutrals.Darkest} />
      <Span type='BodyLargeBold' color={theme.Colors.Neutrals.Darkest} style={{ fontWeight: 700 }}>
        {t({
          id: 'login.switch-hospital',
          message: 'Wissel van ziekenhuis',
        })}
      </Span>
    </StSwitchHospitalButton>
  )
}

export { LoginPage }

// Styled Components
// ------------------------------------------------------------------------- /

const StMainContent = styled(FlexColumn)`
  gap: ${({ theme }) => theme.UI.SpacingPx.Space6};
  flex: 1;
`

const StAppSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
`

const StAppButtons = styled(FlexRow)`
  gap: ${({ theme }) => theme.UI.SpacingPx.Space5};
`

const StMoreInfoLink = styled.a`
  text-decoration: none;
  color: inherit;
`

const StSwitchHospitalButton = styled(FlexRow)`
  justify-content: flex-start;
  align-items: center;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space2};
  cursor: pointer;
`
